import { Button, Divider, Grid, Stack, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { RemoveCircleOutline } from "@mui/icons-material";

import nameof from "ts-nameof.macro";

import AlertExtended from "../../../common/components/mui-extended/AlertExtended";
import SectionExtended from "../../../common/components/mui-extended/SectionExtended";
import TextFieldExtended from "../../../common/components/mui-extended/TextFieldExtended";
import { PublicRoutesConfig } from "../../RoutesConfig";
import { Rules } from "../../../common/validationRules";
import { useAxiosLoader } from "../../../common/hooks/useAxiosLoader";
import {
  ObligationRequestDto,
  PersonTaxpayerType,
  getTaxpayerPinLabel,
  ObligationRequestResultDto,
} from "../../../interfaces/Obligation";
import { useForm } from "react-hook-form";
import { useState } from "react";
import useUserContext from "../../../hooks/useUserContext";
import { Link } from "react-router-dom";
import SelectExtended from "../../../common/components/mui-extended/SelectExtended";
import obligationService from "../../../services/obligationService";
import TaxpayerPinType from "./TaxpayerPinType";
import { dateToStringShort } from "../../../common/utils/dateTime";

export default function Request() {
  const loading = useAxiosLoader(false);
  const { userId } = useUserContext();

  const [searched, setSearched] = useState(false);
  const [requestResult, setRequestResult] = useState<ObligationRequestResultDto>({});

  const defaultModel: ObligationRequestDto = {
    taxpayerPinType: PersonTaxpayerType.Egn,
  };

  const form = useForm<ObligationRequestDto>({
    mode: "all",
    defaultValues: defaultModel,
  });

  const onSubmit = async (model: ObligationRequestDto) => {
    model.userId = userId;
    await obligationService.sendRequestAsync(model).then(({ data }) => {
      setRequestResult(data);
    });

    setSearched(true);
  };

  const renderForm = () => {
    return (
      <form onReset={() => form.reset(defaultModel)} onSubmit={form.handleSubmit(onSubmit)}>
        <Stack spacing={3}>
          <SectionExtended title="Данни на задължено лице">
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TaxpayerPinType name={nameof(defaultModel.taxpayerPinType)} form={form} />
              </Grid>

              <Grid item xs={12} md={6} lg={4}>
                <TextFieldExtended
                  label={getTaxpayerPinLabel(form.watch("taxpayerPinType"))}
                  name={nameof(defaultModel.taxpayerPin)}
                  form={form}
                  rules={{
                    ...Rules.Required,
                    minLength: {
                      value: 9,
                      message: "Трябва да въведете поне 9 символа",
                    },
                  }}
                  onInput={(e: any) => {
                    e.target.value = e.target.value.toString().slice(0, 13);
                  }}
                />
              </Grid>

              <Grid item xs={12} md={12} lg={8}>
                <SelectExtended
                  label="Нотариално производство"
                  name={nameof(defaultModel.documentTypeId)}
                  form={form}
                  searchRequest={obligationService.getDocumentTypes}
                  initRequest={obligationService.getDocumentTypesByIds}
                  rules={Rules.Required}
                />
              </Grid>
            </Grid>
          </SectionExtended>
          <Grid container justifyContent="space-between">
            <Grid item>
              <Button startIcon={<RemoveCircleOutline />} type="reset">
                Изчисти
              </Button>
            </Grid>
            <Grid item>
              <Stack direction="row" spacing={1}>
                <LoadingButton
                  variant="contained"
                  loading={loading}
                  loadingPosition="start"
                  startIcon={PublicRoutesConfig.NapObligations.Request.Icon}
                  type="submit"
                >
                  Заяви проверка
                </LoadingButton>
              </Stack>
            </Grid>
          </Grid>
        </Stack>
      </form>
    );
  };

  const renderSuccess = () => (
    <Stack spacing={2}>
      <Divider sx={{ mb: "10px" }} />
      <AlertExtended type="success" title={"Успешно заявена проверка"}>
        на задължено лице с {requestResult.taxpayerPinType} : <strong>{requestResult.taxpayerPin}</strong>, входящ номер
        на проверката{" "}
        <strong>
          {requestResult.entryNumber} / {dateToStringShort(requestResult.entryDate)}
        </strong>
        .
      </AlertExtended>
      <Grid container>
        <Grid item xs={6}>
          <Button
            component={Link}
            to={PublicRoutesConfig.NapObligations.Request.Path}
            reloadDocument
            startIcon={PublicRoutesConfig.NapObligations.Request.Icon}
          >
            Нова проверка
          </Button>
        </Grid>
        <Grid item textAlign="end" xs={6}>
          <Button
            component={Link}
            to={PublicRoutesConfig.NapObligations.Search.Path}
            state={{ autosearch: true }}
            replace={true}
            startIcon={PublicRoutesConfig.NapObligations.Search.Icon}
          >
            Статус на проверка
          </Button>
        </Grid>
      </Grid>
    </Stack>
  );

  return (
    <Stack spacing={3}>
      <Typography variant="h5">Проверка на задължено лице</Typography>
      {!(searched && requestResult) && renderForm()}
      {searched && requestResult && renderSuccess()}
    </Stack>
  );
}
